import React, { useEffect, useState, useRef } from 'react';
import { useEditorStore } from '../../store/useEditorStore';
import { Point2D } from '../../types';
import CalibrationDialog from '../CalibrationDialog';
import { useTranslation } from 'react-i18next';

interface CalibrationOverlayProps {
  width: number;
  height: number;
  zoom: number;
  offset: Point2D;
}

const CalibrationOverlay: React.FC<CalibrationOverlayProps> = ({ width, height, zoom, offset }) => {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const {
    isCalibrating,
    calibrationPoint1,
    calibrationPoint2,
    setCalibrationPoint1,
    updateCalibrationPoint2,
    finishCalibration,
    cancelCalibration,
    project,
  } = useEditorStore();

  const [mousePos, setMousePos] = useState<Point2D | null>(null);
  const [showDialog, setShowDialog] = useState(false);

  // 处理ESC键取消
  useEffect(() => {
    if (!isCalibrating) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelCalibration();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCalibrating, cancelCalibration]);

  // 绘制校准UI
  useEffect(() => {
    if (!isCalibrating || !overlayRef.current) return;

    const canvas = overlayRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const render = () => {
      // 清空overlay canvas
      ctx.clearRect(0, 0, width, height);
      if (!ctx) return;

      // 绘制测量线（如果有起点）
      if (calibrationPoint1 && calibrationPoint2) {
        ctx.save();
        
        // 绘制虚线
        ctx.strokeStyle = '#1e5a8a'; // 蓝色
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 4]); // 虚线样式
        ctx.beginPath();
        ctx.moveTo(calibrationPoint1.x, calibrationPoint1.y);
        ctx.lineTo(calibrationPoint2.x, calibrationPoint2.y);
        ctx.stroke();
        ctx.setLineDash([]); // 重置虚线
        
        // 绘制起点（小圆点）- 根据zoom动态调整
        const basePointRadius = 3;
        const pointRadius = zoom < 1 ? basePointRadius / zoom : basePointRadius;
        const clampedPointRadius = Math.min(Math.max(pointRadius, basePointRadius), 6);
        
        ctx.fillStyle = '#1e5a8a';
        ctx.beginPath();
        ctx.arc(calibrationPoint1.x, calibrationPoint1.y, clampedPointRadius, 0, 2 * Math.PI);
        ctx.fill();
        
        // 绘制终点（小圆点）
        ctx.beginPath();
        ctx.arc(calibrationPoint2.x, calibrationPoint2.y, clampedPointRadius, 0, 2 * Math.PI);
        ctx.fill();
        
        // 计算并显示底图实际像素长度
        let displayLength = 0;
        if (project?.mapConfig?.baseMap) {
          const baseMap = project.mapConfig.baseMap;
          
          // 将屏幕坐标转换为世界坐标
          const worldX1 = (calibrationPoint1.x - offset.x) / zoom;
          const worldY1 = (calibrationPoint1.y - offset.y) / zoom;
          const worldX2 = (calibrationPoint2.x - offset.x) / zoom;
          const worldY2 = (calibrationPoint2.y - offset.y) / zoom;
          
          // 世界坐标转换为底图像素坐标
          const imageX1 = (worldX1 - baseMap.offset.x) * (baseMap.scale / 100);
          const imageY1 = (worldY1 - baseMap.offset.y) * (baseMap.scale / 100);
          const imageX2 = (worldX2 - baseMap.offset.x) * (baseMap.scale / 100);
          const imageY2 = (worldY2 - baseMap.offset.y) * (baseMap.scale / 100);
          
          // 计算底图像素坐标系中的距离
          displayLength = Math.sqrt(
            Math.pow(imageX2 - imageX1, 2) +
            Math.pow(imageY2 - imageY1, 2)
          );
        } else {
          // 回退：使用屏幕像素（不推荐）
          displayLength = Math.sqrt(
            Math.pow(calibrationPoint2.x - calibrationPoint1.x, 2) +
            Math.pow(calibrationPoint2.y - calibrationPoint1.y, 2)
          );
        }
        
        const midX = (calibrationPoint1.x + calibrationPoint2.x) / 2;
        const midY = (calibrationPoint1.y + calibrationPoint2.y) / 2;
        
        ctx.fillStyle = '#1e5a8a';
        ctx.font = '14px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(`${displayLength.toFixed(2)} px`, midX, midY - 8);
        
        ctx.restore();
      }

      // 绘制跟随鼠标的蓝色光标点
      if (mousePos) {
        ctx.save();
        ctx.fillStyle = '#1e5a8a';
        ctx.beginPath();
        ctx.arc(mousePos.x, mousePos.y, 3, 0, 2 * Math.PI);
        ctx.fill();
        
        // 外圈
        ctx.strokeStyle = '#93c5fd';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(mousePos.x, mousePos.y, 5, 0, 2 * Math.PI);
        ctx.stroke();
        
        ctx.restore();
      }

      requestAnimationFrame(render);
    };

    const animationId = requestAnimationFrame(render);
    return () => cancelAnimationFrame(animationId);
  }, [isCalibrating, calibrationPoint1, calibrationPoint2, mousePos, overlayRef, width, height]);

  if (!isCalibrating) return null;

  // 计算基于底图实际像素的长度（而不是屏幕像素）
  const pixelLength = (() => {
    if (!calibrationPoint1 || !calibrationPoint2 || !project?.mapConfig?.baseMap) {
      return 0;
    }

    const baseMap = project.mapConfig.baseMap;
    
    // 将屏幕坐标转换为世界坐标（考虑缩放和偏移）
    const worldX1 = (calibrationPoint1.x - offset.x) / zoom;
    const worldY1 = (calibrationPoint1.y - offset.y) / zoom;
    const worldX2 = (calibrationPoint2.x - offset.x) / zoom;
    const worldY2 = (calibrationPoint2.y - offset.y) / zoom;
    
    // 世界坐标转换为底图像素坐标
    // 世界坐标系中，底图的scale定义了像素到世界单位的转换
    // worldWidth = baseMap.width * (100 / baseMap.scale)
    // 所以：imagePixel = worldCoord * (baseMap.scale / 100)
    const imageX1 = (worldX1 - baseMap.offset.x) * (baseMap.scale / 100);
    const imageY1 = (worldY1 - baseMap.offset.y) * (baseMap.scale / 100);
    const imageX2 = (worldX2 - baseMap.offset.x) * (baseMap.scale / 100);
    const imageY2 = (worldY2 - baseMap.offset.y) * (baseMap.scale / 100);
    
    // 计算底图像素坐标系中的距离
    const length = Math.sqrt(
      Math.pow(imageX2 - imageX1, 2) +
      Math.pow(imageY2 - imageY1, 2)
    );
    
    console.log('📏 校准测量:', {
      screen: { x1: calibrationPoint1.x, y1: calibrationPoint1.y, x2: calibrationPoint2.x, y2: calibrationPoint2.y },
      world: { x1: worldX1.toFixed(2), y1: worldY1.toFixed(2), x2: worldX2.toFixed(2), y2: worldY2.toFixed(2) },
      image: { x1: imageX1.toFixed(2), y1: imageY1.toFixed(2), x2: imageX2.toFixed(2), y2: imageY2.toFixed(2) },
      length: length.toFixed(2) + ' px (底图实际像素)'
    });
    
    return length;
  })();

  return (
    <>
      {/* Overlay Canvas - 只用于绘制，不拦截事件 */}
      <canvas
        ref={overlayRef}
        width={width}
        height={height}
        className="absolute top-0 left-0"
        style={{ 
          zIndex: 100, 
          pointerEvents: 'none' // 不拦截任何事件
        }}
      />
      
      {/* 透明交互层 - 捕获点击和移动，但允许滚轮穿透 */}
      <div
        className="absolute top-0 left-0"
        style={{
          width: `${width}px`,
          height: `${height}px`,
          zIndex: 101,
          cursor: 'crosshair',
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const point = { x, y };
          
          setMousePos(point);
          
          if (calibrationPoint1) {
            updateCalibrationPoint2(point);
          }
        }}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const point = { x, y };

          if (!calibrationPoint1) {
            setCalibrationPoint1(point);
          } else {
            setShowDialog(true);
          }
        }}
        onWheel={(e) => {
          // 让滚轮事件穿透到Canvas2D
          e.stopPropagation();
          const canvas2D = document.querySelector('canvas[data-canvas="2d"]') as HTMLCanvasElement;
          if (canvas2D) {
            const event = new WheelEvent('wheel', {
              deltaY: e.deltaY,
              clientX: e.clientX,
              clientY: e.clientY,
              bubbles: true,
              cancelable: true,
            });
            canvas2D.dispatchEvent(event);
          }
        }}
      />
      
      {/* 提示文字 */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[102] pointer-events-none">
        <div className="bg-primary-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
            <span className="font-medium">
              {!calibrationPoint1 
                ? t('calibration.clickFirstPoint') 
                : t('calibration.clickSecondPoint')}
            </span>
          </div>
          <div className="text-xs opacity-75">{t('calibration.pressEscCancel')}</div>
        </div>
      </div>

      {/* 校准对话框 */}
      <CalibrationDialog
        visible={showDialog}
        pixelLength={pixelLength}
        onConfirm={(realLength) => {
          finishCalibration(realLength, pixelLength);
          setShowDialog(false);
        }}
        onCancel={() => {
          setShowDialog(false);
          cancelCalibration();
        }}
      />
    </>
  );
};

export default CalibrationOverlay;
